import React, { useEffect, useMemo, useRef, useState } from 'react'
import '../styles/GalleryDownloads.css'
import '../styles/GallerySelection.css'
import '../styles/GalleryUploaderNames.css'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''
const LONG_PRESS_MS = 550
const LONG_PRESS_MOVE_TOLERANCE = 12

function apiUrl(value) {
  const urlPath = String(value || '')
  if (!urlPath) return ''
  if (/^https?:\/\//i.test(urlPath)) return urlPath
  return `${API_BASE_URL}${urlPath.startsWith('/') ? urlPath : `/${urlPath}`}`
}

function filenameFromDisposition(headerValue, fallbackName) {
  const value = String(headerValue || '')
  const utf8Match = value.match(/filename\*=UTF-8''([^;]+)/i)
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].replace(/["']/g, ''))
    } catch {
      // Use regular filename or fallback below.
    }
  }

  const regularMatch = value.match(/filename="?([^";]+)"?/i)
  return regularMatch?.[1] || fallbackName
}

async function responseErrorMessage(response, fallbackMessage) {
  const contentType = response.headers.get('content-type') || ''
  if (contentType.includes('application/json')) {
    const data = await response.json().catch(() => ({}))
    return data.message || fallbackMessage
  }

  const text = await response.text().catch(() => '')
  return text.trim() || fallbackMessage
}

function saveBlob(blob, filename) {
  const objectUrl = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = filename
  anchor.style.display = 'none'
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
}

function photoKey(photo) {
  return `${photo?.submissionId || ''}:${photo?.id || ''}`
}

function uploaderName(photo) {
  return String(photo?.guestName || '').trim() || 'Unknown uploader'
}

export default function GalleryTab({ accessCode }) {
  const [selectedAlbum, setSelectedAlbum] = useState(null)
  const [photos, setPhotos] = useState([])
  const [loading, setLoading] = useState(true)
  const [photosError, setPhotosError] = useState('')
  const [lightboxIdx, setLightboxIdx] = useState(null)
  const [downloadState, setDownloadState] = useState({ type: '', photoId: '' })
  const [downloadError, setDownloadError] = useState('')
  const [selectionMode, setSelectionMode] = useState(false)
  const [selectedKeys, setSelectedKeys] = useState([])
  const [selectionAction, setSelectionAction] = useState({ status: '', message: '' })

  const longPressTimerRef = useRef(null)
  const longPressStartRef = useRef(null)
  const suppressNextClickRef = useRef(false)

  const selectedKeySet = useMemo(() => new Set(selectedKeys), [selectedKeys])
  const selectedPhotos = useMemo(
    () => photos.filter((photo) => selectedKeySet.has(photoKey(photo))),
    [photos, selectedKeySet]
  )

  useEffect(() => {
    if (!accessCode) {
      setLoading(false)
      return
    }

    setLoading(true)
    setPhotosError('')

    fetch(`${API_BASE_URL}/rsvp/public-summary?code=${encodeURIComponent(accessCode)}`)
      .then(async (response) => {
        const json = await response.json()
        if (!response.ok) throw new Error(json.message || 'Could not resolve invitation.')
        return json
      })
      .then((json) => {
        const invitationId = json.invitationId
        if (!invitationId) throw new Error('Could not resolve invitation.')

        const galleryUrl = `${API_BASE_URL}/photo-albums/${encodeURIComponent(invitationId)}/photos?code=${encodeURIComponent(accessCode)}`
        return fetch(galleryUrl).then(async (response) => {
          const albumJson = await response.json()
          if (!response.ok) throw new Error(albumJson.message || 'Failed to load gallery.')
          return { albumJson, invitationId }
        })
      })
      .then(({ albumJson, invitationId }) => {
        if (!Array.isArray(albumJson.photos)) {
          setPhotos([])
          setSelectedAlbum(null)
          return
        }

        setPhotos(albumJson.photos)
        setSelectedAlbum({
          id: albumJson.albumId || invitationId,
          title: albumJson.title || invitationId,
          organizerView: Boolean(albumJson.organizerView),
          downloadAllPath: albumJson.downloadAllPath || '',
        })
        setSelectionMode(false)
        setSelectedKeys([])
      })
      .catch((err) => setPhotosError(err.message || 'Failed to load gallery.'))
      .finally(() => setLoading(false))
  }, [accessCode])

  useEffect(() => {
    function onKey(event) {
      if (event.key === 'Escape') {
        if (selectionMode) clearSelection()
        else if (lightboxIdx !== null) setLightboxIdx(null)
        return
      }

      if (selectionMode || lightboxIdx === null) return
      if (event.key === 'ArrowLeft') prevPhoto()
      else if (event.key === 'ArrowRight') nextPhoto()
    }

    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [lightboxIdx, photos.length, selectionMode])

  useEffect(() => () => clearLongPress(), [])

  function getPreviewFilename(photo) {
    return photo?.previewFilename || photo?.filename || ''
  }

  function getDownloadFilename(photo) {
    return photo?.downloadFilename || ''
  }

  function photoPreviewUrl(photo) {
    if (photo?.previewPath) return apiUrl(photo.previewPath)
    const filename = getPreviewFilename(photo)
    if (!filename || !selectedAlbum?.id) return ''
    return `${API_BASE_URL}/photo-albums/${encodeURIComponent(selectedAlbum.id)}/files/${encodeURIComponent(filename)}`
  }

  function photoDownloadUrl(photo) {
    if (photo?.downloadPath) return apiUrl(photo.downloadPath)
    const filename = getDownloadFilename(photo)
    if (!filename || !selectedAlbum?.id) return ''
    return `${API_BASE_URL}/photo-albums/${encodeURIComponent(selectedAlbum.id)}/files/${encodeURIComponent(filename)}/download`
  }

  function clearLongPress() {
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current)
      longPressTimerRef.current = null
    }
    longPressStartRef.current = null
  }

  function startSelection(index) {
    const photo = photos[index]
    if (!photo) return
    clearLongPress()
    setLightboxIdx(null)
    setSelectionMode(true)
    setSelectedKeys((current) => current.includes(photoKey(photo)) ? current : [...current, photoKey(photo)])
    setSelectionAction({ status: '', message: '' })
  }

  function beginLongPress(event, index) {
    if (event.pointerType === 'mouse' && event.button !== 0) return
    clearLongPress()
    longPressStartRef.current = { x: event.clientX, y: event.clientY }
    longPressTimerRef.current = window.setTimeout(() => {
      suppressNextClickRef.current = true
      startSelection(index)
      if (navigator.vibrate) navigator.vibrate(30)
    }, LONG_PRESS_MS)
  }

  function handleLongPressMove(event) {
    const start = longPressStartRef.current
    if (!start) return
    const moved = Math.hypot(event.clientX - start.x, event.clientY - start.y)
    if (moved > LONG_PRESS_MOVE_TOLERANCE) clearLongPress()
  }

  function toggleSelected(index) {
    const key = photoKey(photos[index])
    if (!key || key === ':') return
    setSelectedKeys((current) => (
      current.includes(key) ? current.filter((item) => item !== key) : [...current, key]
    ))
  }

  function handleThumbnailClick(index) {
    clearLongPress()
    if (suppressNextClickRef.current) {
      suppressNextClickRef.current = false
      return
    }

    if (selectionMode) toggleSelected(index)
    else setLightboxIdx(index)
  }

  function clearSelection() {
    clearLongPress()
    setSelectionMode(false)
    setSelectedKeys([])
    setSelectionAction({ status: '', message: '' })
  }

  function selectAll() {
    setSelectedKeys(photos.map(photoKey).filter((key) => key && key !== ':'))
  }

  async function downloadFromApi(url, fallbackFilename, options = {}) {
    const response = await fetch(url, {
      method: options.method || 'GET',
      headers: {
        Accept: 'application/octet-stream',
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      },
      body: options.body ? JSON.stringify(options.body) : undefined,
    })

    if (!response.ok) {
      throw new Error(await responseErrorMessage(response, 'Download failed. Please try again.'))
    }

    const blob = await response.blob()
    if (!blob.size) throw new Error('The downloaded file is empty.')

    const filename = filenameFromDisposition(
      response.headers.get('content-disposition'),
      fallbackFilename
    )
    saveBlob(blob, filename)
  }

  async function handleDownloadZip() {
    if (!selectedAlbum?.id || downloadState.type) return

    setDownloadError('')
    setDownloadState({ type: 'zip', photoId: '' })

    try {
      const fallbackPath = `/photo-albums/${encodeURIComponent(selectedAlbum.id)}/download`
      await downloadFromApi(
        apiUrl(selectedAlbum.downloadAllPath || fallbackPath),
        `${selectedAlbum.id}-all-photos.zip`
      )
    } catch (err) {
      setDownloadError(err.message || 'Could not download the ZIP file.')
    } finally {
      setDownloadState({ type: '', photoId: '' })
    }
  }

  async function handleDownloadPhoto(photo, index) {
    const url = photoDownloadUrl(photo)
    if (!url || !selectedAlbum?.id || downloadState.type) return

    const id = photo.id || getDownloadFilename(photo)
    setDownloadError('')
    setDownloadState({ type: 'photo', photoId: id })

    try {
      await downloadFromApi(url, `${selectedAlbum.id}-photo-${index + 1}.jpg`)
    } catch (err) {
      setDownloadError(err.message || 'Could not download this photo.')
    } finally {
      setDownloadState({ type: '', photoId: '' })
    }
  }

  async function handleDownloadSelected() {
    if (!selectedAlbum?.id || selectedPhotos.length === 0 || selectionAction.status === 'working') return

    setSelectionAction({ status: 'working', message: 'Preparing selected photos…' })
    try {
      const url = `${API_BASE_URL}/photo-albums/${encodeURIComponent(selectedAlbum.id)}/client/selection/download?code=${encodeURIComponent(accessCode)}`
      await downloadFromApi(url, `${selectedAlbum.id}-selected-photos.zip`, {
        method: 'POST',
        body: {
          photos: selectedPhotos.map((photo) => ({
            submissionId: photo.submissionId,
            fileId: photo.id,
          })),
        },
      })
      clearSelection()
    } catch (err) {
      setSelectionAction({ status: 'error', message: err.message || 'Could not download selected photos.' })
    }
  }

  async function updateSelectedPrivacy(nextPrivacy) {
    if (selectedPhotos.length === 0 || selectionAction.status === 'working') return
    const normalizedPrivacy = nextPrivacy === 'private' ? 'private' : 'public'

    setSelectionAction({
      status: 'working',
      message: `Setting ${selectedPhotos.length} photo${selectedPhotos.length === 1 ? '' : 's'} to ${normalizedPrivacy}…`,
    })

    try {
      await Promise.all(selectedPhotos.map(async (photo) => {
        const url = apiUrl(photo.privacyPath)
        if (!url) throw new Error('Privacy route is unavailable for a selected photo.')

        const response = await fetch(url, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ privacy: normalizedPrivacy }),
        })
        const data = await response.json().catch(() => ({}))
        if (!response.ok || data.ok === false) {
          throw new Error(data.message || 'Unable to update selected photo privacy.')
        }
      }))

      setPhotos((current) => current.map((photo) => (
        selectedKeySet.has(photoKey(photo)) ? { ...photo, privacy: normalizedPrivacy } : photo
      )))
      clearSelection()
    } catch (err) {
      setSelectionAction({ status: 'error', message: err.message || 'Unable to update selected photos.' })
    }
  }

  function prevPhoto() {
    setLightboxIdx((index) => (index - 1 + photos.length) % photos.length)
  }

  function nextPhoto() {
    setLightboxIdx((index) => (index + 1) % photos.length)
  }

  if (loading) return <div className="gallery-loading-msg">Loading gallery…</div>

  if (photosError) {
    return (
      <div className="gallery-empty-state">
        <div className="gallery-empty-icon">⚠️</div>
        <h3>Could not load gallery</h3>
        <p>{photosError}</p>
      </div>
    )
  }

  if (!selectedAlbum) {
    return (
      <div className="gallery-empty-state">
        <div className="gallery-empty-icon">📷</div>
        <h3>No Photo Album Yet</h3>
        <p>Event photos will appear here once an album is set up for your invitation.</p>
      </div>
    )
  }

  const activePhoto = lightboxIdx !== null ? photos[lightboxIdx] : null
  const activePhotoId = activePhoto?.id || getDownloadFilename(activePhoto)
  const hasDownloadablePhotos = photos.some((photo) => Boolean(photoDownloadUrl(photo)))
  const downloadingZip = downloadState.type === 'zip'
  const downloadingActivePhoto = downloadState.type === 'photo' && downloadState.photoId === activePhotoId
  const privateCount = photos.filter((photo) => photo.privacy === 'private').length
  const publicCount = photos.length - privateCount
  const selectionBusy = selectionAction.status === 'working'

  return (
    <div className={`gallery-view${selectionMode ? ' gallery-selection-mode' : ''}`}>
      <div className="gallery-toolbar">
        <div className="gallery-toolbar-center">
          <span className="gallery-toolbar-title">{selectedAlbum.title}</span>
          <span className="gallery-photo-count">
            {photos.length} photo{photos.length !== 1 ? 's' : ''}
            {selectedAlbum.organizerView && ` · ${publicCount} public · ${privateCount} private`}
          </span>
          {selectedAlbum.organizerView && !selectionMode && (
            <span className="gallery-privacy-help">Long-press or right-click a photo to select multiple images.</span>
          )}
        </div>
        {!selectionMode && hasDownloadablePhotos && (
          <button
            type="button"
            className="btn primary gallery-zip-btn"
            onClick={handleDownloadZip}
            disabled={Boolean(downloadState.type)}
          >
            {downloadingZip ? 'Preparing ZIP…' : '⬇ Download All (ZIP)'}
          </button>
        )}
      </div>

      {selectionMode && (
        <section className="gallery-selection-toolbar" aria-label="Selected photo actions">
          <div className="gallery-selection-summary">
            <strong>{selectedPhotos.length} selected</strong>
            <span>Tap more photos to add or remove them.</span>
          </div>
          <div className="gallery-selection-actions">
            <button type="button" onClick={selectAll} disabled={selectionBusy || selectedPhotos.length === photos.length}>Select All</button>
            <button type="button" onClick={() => updateSelectedPrivacy('public')} disabled={selectionBusy || selectedPhotos.length === 0}>Set Public</button>
            <button type="button" onClick={() => updateSelectedPrivacy('private')} disabled={selectionBusy || selectedPhotos.length === 0}>Set Private</button>
            <button type="button" className="primary" onClick={handleDownloadSelected} disabled={selectionBusy || selectedPhotos.length === 0}>
              {selectionBusy ? 'Working…' : 'Download Selected'}
            </button>
            <button type="button" className="cancel" onClick={clearSelection} disabled={selectionBusy}>Cancel</button>
          </div>
          {selectionAction.message && (
            <p className={`gallery-selection-message gallery-selection-message-${selectionAction.status}`} role="status">
              {selectionAction.message}
            </p>
          )}
        </section>
      )}

      {downloadError && <p className="gallery-download-error" role="alert">{downloadError}</p>}

      {photos.length === 0 && (
        <div className="gallery-empty-state">
          <div className="gallery-empty-icon">📷</div>
          <h3>No Photos Yet</h3>
          <p>Uploaded event photos will appear here.</p>
        </div>
      )}

      {photos.length > 0 && (
        <div className="gallery-grid">
          {photos.map((photo, idx) => {
            const isSelected = selectedKeySet.has(photoKey(photo))
            const uploadedBy = uploaderName(photo)
            const actionLabel = selectionMode
              ? `${isSelected ? 'Deselect' : 'Select'} photo ${idx + 1} uploaded by ${uploadedBy}`
              : `View photo ${idx + 1} uploaded by ${uploadedBy}`

            return (
              <button
                key={photo.id || getPreviewFilename(photo)}
                type="button"
                className={`gallery-grid-item${isSelected ? ' gallery-grid-item-selected' : ''}`}
                onPointerDown={(event) => beginLongPress(event, idx)}
                onPointerMove={handleLongPressMove}
                onPointerUp={clearLongPress}
                onPointerCancel={clearLongPress}
                onPointerLeave={clearLongPress}
                onContextMenu={(event) => {
                  event.preventDefault()
                  suppressNextClickRef.current = true
                  startSelection(idx)
                }}
                onClick={() => handleThumbnailClick(idx)}
                aria-pressed={selectionMode ? isSelected : undefined}
                aria-label={actionLabel}
              >
                <img
                  src={photoPreviewUrl(photo)}
                  alt={`Event photo ${idx + 1} uploaded by ${uploadedBy}`}
                  loading="lazy"
                  decoding="async"
                  draggable="false"
                  className="gallery-thumbnail"
                />
                <span className={`gallery-privacy-badge gallery-privacy-${photo.privacy || 'public'}`}>
                  {photo.privacy === 'private' ? 'Private' : 'Public'}
                </span>
                <span className="gallery-uploader-name" title={uploadedBy}>
                  {uploadedBy}
                </span>
                {selectionMode && (
                  <span className={`gallery-selection-check${isSelected ? ' selected' : ''}`} aria-hidden="true">
                    {isSelected ? '✓' : ''}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      )}

      {activePhoto && !selectionMode && (
        <div className="gallery-lightbox" role="dialog" aria-modal="true" onClick={() => setLightboxIdx(null)}>
          <button className="gallery-lb-close" onClick={() => setLightboxIdx(null)} aria-label="Close lightbox">✕</button>
          <button className="gallery-lb-nav prev" onClick={(event) => { event.stopPropagation(); prevPhoto() }} aria-label="Previous photo">‹</button>
          <img
            className="gallery-lb-img"
            src={photoPreviewUrl(activePhoto)}
            alt={`Photo ${lightboxIdx + 1} of ${photos.length}, uploaded by ${uploaderName(activePhoto)}`}
            decoding="async"
            draggable="false"
            onClick={(event) => event.stopPropagation()}
          />
          <button className="gallery-lb-nav next" onClick={(event) => { event.stopPropagation(); nextPhoto() }} aria-label="Next photo">›</button>

          <div className="gallery-lb-footer" onClick={(event) => event.stopPropagation()}>
            <span className="gallery-lb-counter">
              {lightboxIdx + 1} / {photos.length} · {activePhoto.privacy === 'private' ? 'Private' : 'Public'}
            </span>
            <span className="gallery-lb-uploader-name" title={uploaderName(activePhoto)}>
              Uploaded by {uploaderName(activePhoto)}
            </span>
            <button
              type="button"
              className="gallery-lb-privacy-button"
              onClick={() => startSelection(lightboxIdx)}
            >
              Select / Privacy
            </button>
            {photoDownloadUrl(activePhoto) && (
              <button
                type="button"
                className="gallery-lb-download"
                onClick={() => handleDownloadPhoto(activePhoto, lightboxIdx)}
                disabled={Boolean(downloadState.type)}
              >
                {downloadingActivePhoto ? 'Downloading…' : '⬇ Download JPG'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
